import { useRef, useEffect } from 'preact/hooks'
import { getPBProps, sanitizeHtml } from '@content/helpers/src/utils.ts'
import { PBProps } from './types'
import { PbPropsProvider } from '@content/helpers/src/context'
import { pushPageView } from '@content/helpers/src/gtmEvents'
import { getDataLayer, isLoggedIn } from '@content/helpers/src/dataLayer'
import Background from '@commons/background/Background'
import SectionHeader from '@commons/section-header/SectionHeader'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker'
import HeroDoubleCta from '@components/member-get-member/mgm-hero/subcomponents/HeroDoubleCta/HeroDoubleCta.tsx'
import styles from './MgmHero.module.scss'
import { getTestId } from '@content/helpers/src/utils'

type BackgroundType = {
    mobile?: string | { src: string }
    desktop?: string | { src: string }
    retina?: string | { src: string }
}

type HeroHomeProps = {
    data: string
}

function MgmHero({ data }: HeroHomeProps) {
    const pbProps = getPBProps<PBProps>(data)
    const { campaign, layout, copywriting } = pbProps as PBProps
    const containerRef = useRef(null)

    useImpressionTracker(containerRef, campaign)

    useEffect(() => {
        const header = document.getElementById('header') as HTMLElement
        const countryMsg = document.querySelector(
            '.HeaderCountryMessage'
        ) as HTMLElement

        const headerHeight = header?.offsetHeight || 200
        const countryMsgHeight = countryMsg?.offsetHeight || 0

        document.documentElement.style.setProperty(
            '--hero-offset-height',
            `${headerHeight + countryMsgHeight}px`
        )

        const dataLayer = getDataLayer()
        const page_name = dataLayer?.page?.pageInfo?.pageName
        const breadcrumb_id = dataLayer?.page?.pageInfo?.breadcrumbUID
        const page_type = dataLayer?.page?.pageInfo?.type
        const page_category = dataLayer?.page?.pageInfo?.primaryCategory
        const page_subcategory = dataLayer?.page?.pageInfo?.secondaryCategory
        const page_technology = dataLayer?.page?.pageInfo?.technology
        const landscape = dataLayer?.app?.landscape
        const prod_environment = dataLayer?.app?.isEnvironmentProd
        const market = dataLayer?.app?.market
        const language = dataLayer?.page?.pageInfo?.language.toLocaleLowerCase()
        const business_segment = dataLayer?.page?.pageInfo?.segmentBusiness
        const loggedIn = isLoggedIn() ? true : false
        const club_member_id = loggedIn
            ? dataLayer?.user?.clubMember?.id
            : undefined

        pushPageView({
            breadcrumb_id: breadcrumb_id,
            page_name: page_name,
            page_type: page_type,
            page_category: page_category,
            page_subcategory: page_subcategory,
            page_technology: page_technology,
            market: market,
            language: language,
            prod_environment: prod_environment,
            landscape: landscape,
            business_segment: business_segment,
            club_member_id: club_member_id,
            club_member_login_status: loggedIn
        })
    }, [])

    const getBackground = (background?: BackgroundType) => ({
        mobile:
            typeof background?.mobile === 'string'
                ? background.mobile
                : background?.mobile?.src,
        desktop:
            typeof background?.desktop === 'string'
                ? { src: background.desktop }
                : background?.desktop,
        retina:
            typeof background?.retina === 'string'
                ? { src: background.retina }
                : background?.retina
    })

    return (
        <section
            ref={containerRef}
            className={styles.mgmHero}
            data-testid={getTestId('mgm-hero')}>
            <PbPropsProvider value={pbProps}>
                <div className={styles.mgmHero__container}>
                    <Background
                        overlay=""
                        {...getBackground(layout?.background)}
                        className={styles.imageWrapper}
                    />

                    <div className={styles.mgmHeroWrapper}>
                        <div className={styles.headingWrapper}>
                            <SectionHeader
                                headlineTag="h2"
                                contrast={layout?.contrast}
                                headline={
                                    <h2
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(
                                                copywriting?.heading
                                            )
                                        }}
                                    />
                                }
                                subhead={copywriting?.subhead}
                                description={copywriting?.description}
                                className={styles.sectionHeaderStyle}
                            />
                        </div>
                    </div>
                </div>

                {copywriting?.doubleCta && (
                    <div className={styles.mgmDoubleCtaContainer}>
                        <HeroDoubleCta
                            image={copywriting.doubleCta.image}
                            ctas={[
                                copywriting.doubleCta.cta1,
                                copywriting.doubleCta.cta2
                            ]}
                        />
                    </div>
                )}
            </PbPropsProvider>
        </section>
    )
}

export default MgmHero
